import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { 
    Plus, 
    Pencil, 
    Trash2, 
    Image as ImageIcon,
    Loader2,
    CheckCircle2,
    XCircle,
    Eye,
    Search,
    ChevronLeft,
    ChevronRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../../components/ui/dialog";
import { toast } from 'sonner';
import MediaPicker from '../../components/MediaPicker';

// React Quill for Rich Text
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const NewsManagement = () => {
    const { getToken } = useAuth();
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);
    
    // Pagination & Filter State
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    // Form State
    const [formData, setFormData] = useState({
        news_id: null,
        title: '',
        excerpt: '',
        content: '',
        image_url: '',
        is_published: false
    });
    
    useEffect(() => {
        fetchNews();
    }, [page, limit, statusFilter]);

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            if (page !== 1) {
                setPage(1);
            } else {
                fetchNews();
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchNews = async () => {
        try {
            setLoading(true);
            const params = {
                page,
                limit
            };
            if (statusFilter === 'published') params.is_published = true;
            if (statusFilter === 'draft') params.is_published = false;
            if (search.trim()) params.search = search.trim();

            const response = await axios.get(`${API_URL}/news`, {
                headers: { Authorization: `Bearer ${getToken()}` },
                params
            });
            const data = response.data;

            if (Array.isArray(data)) {
                setNews(data);
                setTotalCount(data.length);
                setTotalPages(Math.ceil(data.length / limit) || 1);
            } else {
                setNews(data.news || []);
                setTotalCount(data.total || 0);
                setTotalPages(data.pages || 1);
            }
        } catch (error) {
            toast.error('Gagal mengambil data berita');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (post = null) => {
        if (post) {
            setFormData({
                news_id: post.news_id,
                title: post.title,
                excerpt: post.excerpt || '',
                content: post.content || '',
                image_url: post.image_url || '',
                is_published: post.is_published
            });
        } else {
            setFormData({
                news_id: null,
                title: '',
                excerpt: '',
                content: '',
                image_url: '',
                is_published: false
            });
        }
        setIsDialogOpen(true);
    };

    const handleSave = async () => {
        if (!formData.title || !formData.content) {
            toast.error('Judul dan konten wajib diisi');
            return;
        }

        try {
            if (formData.news_id) {
                // Update
                await axios.put(`${API_URL}/news/${formData.news_id}`, formData, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                });
                toast.success('Berita berhasil diperbarui');
            } else {
                // Create
                const payload = { ...formData };
                delete payload.news_id;
                await axios.post(`${API_URL}/news`, payload, {
                    headers: { Authorization: `Bearer ${getToken()}` }
                });
                toast.success('Berita berhasil dibuat');
            }
            setIsDialogOpen(false);
            fetchNews();
        } catch (error) {
            toast.error('Gagal menyimpan berita');
        }
    };

    const handleDelete = async (news_id) => {
        if (!window.confirm('Yakin ingin menghapus berita ini?')) return;
        
        try {
            await axios.delete(`${API_URL}/news/${news_id}`, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success('Berita berhasil dihapus');
            if (news.length === 1 && page > 1) {
                setPage(p => p - 1);
            } else {
                fetchNews();
            }
        } catch (error) {
            toast.error('Gagal menghapus berita');
        }
    };

    const handleTogglePublish = async (post) => {
        try {
            await axios.put(`${API_URL}/news/${post.news_id}`, {
                is_published: !post.is_published
            }, {
                headers: { Authorization: `Bearer ${getToken()}` }
            });
            toast.success(`Berita berhasil di${!post.is_published ? 'publish' : 'unpublish'}`);
            fetchNews();
        } catch (error) {
            toast.error('Gagal mengubah status publish');
        }
    };

    const handleMediaSelect = (media) => {
        setFormData(prev => ({ ...prev, image_url: media.secure_url }));
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{'list': 'ordered'}, {'list': 'bullet'}],
            ['link'],
            ['clean']
        ],
    };

    const getPaginationRange = () => {
        const delta = 1;
        const range = [];
        const rangeWithDots = [];

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
                range.push(i);
            }
        }

        let prev = 0;
        for (const i of range) {
            if (prev) {
                if (i - prev === 2) {
                    rangeWithDots.push(prev + 1);
                } else if (i - prev !== 1) {
                    rangeWithDots.push('...');
                }
            }
            rangeWithDots.push(i);
            prev = i;
        }

        return rangeWithDots;
    };

    const startItem = totalCount === 0 ? 0 : (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, totalCount);

    return (
        <div className="space-y-6 max-w-7xl mx-auto">
            <div className="flex justify-between items-center bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Manajemen Berita</h1>
                    <p className="text-gray-500 mt-1">Kelola berita, artikel, atau pengumuman</p>
                </div>
                <Button onClick={() => handleOpenDialog()} className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-2">
                    <Plus className="w-4 h-4" /> Tambah Berita
                </Button>
            </div>

            {/* Filter & Search Bar */}
            <div className="flex flex-col md:flex-row gap-3 justify-between items-stretch md:items-center bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        placeholder="Cari berita berdasarkan judul..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1);
                        }}
                        className="px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700"
                    >
                        <option value="all">Semua Status</option>
                        <option value="published">Hanya Published</option>
                        <option value="draft">Hanya Draft</option>
                    </select>
                    <select
                        value={limit}
                        onChange={(e) => {
                            setLimit(Number(e.target.value));
                            setPage(1);
                        }}
                        className="px-3 py-2 text-sm border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 text-gray-700"
                    >
                        <option value="10">10 / hal</option>
                        <option value="15">15 / hal</option>
                        <option value="25">25 / hal</option>
                        <option value="50">50 / hal</option>
                    </select>
                </div>
            </div>

            {/* List */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50 border-b border-gray-200 text-sm font-semibold text-gray-600 uppercase tracking-wider">
                                <th className="p-4">Thumbnail</th>
                                <th className="p-4">Judul</th>
                                <th className="p-4">Status</th>
                                <th className="p-4">Tanggal Dibuat</th>
                                <th className="p-4 text-right">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                                        Memuat data...
                                    </td>
                                </tr>
                            ) : news.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-500">
                                        {search || statusFilter !== 'all' ? 'Tidak ada berita yang cocok dengan filter.' : 'Belum ada berita.'}
                                    </td>
                                </tr>
                            ) : (
                                news.map(post => (
                                    <tr key={post.news_id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            {post.image_url ? (
                                                <img src={post.image_url} alt={post.title} className="w-16 h-16 object-cover rounded-md" />
                                            ) : (
                                                <div className="w-16 h-16 bg-gray-100 flex items-center justify-center rounded-md text-gray-400">
                                                    <ImageIcon className="w-6 h-6" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <p className="font-semibold text-gray-800">{post.title}</p>
                                            <p className="text-sm text-gray-500 truncate max-w-xs">{post.excerpt}</p>
                                        </td>
                                        <td className="p-4">
                                            <button 
                                                onClick={() => handleTogglePublish(post)}
                                                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                                    post.is_published 
                                                        ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' 
                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                }`}
                                            >
                                                {post.is_published ? (
                                                    <><CheckCircle2 className="w-3.5 h-3.5" /> Published</>
                                                ) : (
                                                    <><XCircle className="w-3.5 h-3.5" /> Draft</>
                                                )}
                                            </button>
                                        </td>
                                        <td className="p-4 text-sm text-gray-600">
                                            {new Date(post.created_at).toLocaleDateString('id-ID')}
                                        </td>
                                        <td className="p-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <a href={`/news/${post.slug}`} target="_blank" rel="noreferrer" className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                                                    <Eye className="w-4 h-4" />
                                                </a>
                                                <button onClick={() => handleOpenDialog(post)} className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => handleDelete(post.news_id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Table Footer Pagination */}
                <div className="flex flex-col sm:flex-row items-center justify-between p-4 bg-gray-50 border-t border-gray-200 text-sm gap-3">
                    <div className="text-gray-500">
                        {totalCount > 0 ? (
                            <>Menampilkan <span className="font-semibold text-gray-800">{startItem}</span>–<span className="font-semibold text-gray-800">{endItem}</span> dari <span className="font-semibold text-gray-800">{totalCount}</span> berita</>
                        ) : (
                            '0 berita ditemukan'
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="flex items-center gap-1.5">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="h-8 px-2.5 text-xs"
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" /> Prev
                            </Button>

                            <div className="flex items-center gap-1">
                                {getPaginationRange().map((pItem, idx) => {
                                    if (pItem === '...') {
                                        return <span key={`admin-dots-${idx}`} className="px-2 text-gray-400 text-xs">...</span>;
                                    }
                                    const isCurrent = pItem === page;
                                    return (
                                        <button
                                            key={`admin-page-${pItem}`}
                                            onClick={() => setPage(pItem)}
                                            className={`min-w-[32px] h-8 px-2 rounded-md text-xs font-medium transition-all ${
                                                isCurrent
                                                    ? 'bg-emerald-600 text-white shadow-sm'
                                                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100'
                                            }`}
                                        >
                                            {pItem}
                                        </button>
                                    );
                                })}
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="h-8 px-2.5 text-xs"
                            >
                                Next <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Dialog Form */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{formData.news_id ? 'Edit Berita' : 'Tambah Berita'}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-6 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Judul Berita</label>
                            <input
                                type="text"
                                value={formData.title}
                                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="Masukkan judul..."
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Cuplikan (Excerpt)</label>
                            <textarea
                                value={formData.excerpt}
                                onChange={(e) => setFormData(prev => ({...prev, excerpt: e.target.value}))}
                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 h-20"
                                placeholder="Teks singkat yang muncul di daftar berita..."
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Gambar Cover (Opsional)</label>
                            <div className="flex items-start gap-4">
                                {formData.image_url ? (
                                    <div className="relative group">
                                        <img src={formData.image_url} alt="Cover" className="w-32 h-32 object-cover rounded-lg border" />
                                        <button 
                                            onClick={() => setFormData(prev => ({...prev, image_url: ''}))}
                                            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                        >
                                            <XCircle className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="w-32 h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400">
                                        <ImageIcon className="w-8 h-8" />
                                    </div>
                                )}
                                
                                <Dialog open={isMediaPickerOpen} onOpenChange={setIsMediaPickerOpen}>
                                    <DialogTrigger asChild>
                                        <Button variant="outline" className="mt-auto">
                                            {formData.image_url ? 'Ganti Gambar' : 'Pilih Gambar'}
                                        </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-4xl">
                                        <DialogHeader>
                                            <DialogTitle>Pilih Media dari Cloudinary</DialogTitle>
                                        </DialogHeader>
                                        <MediaPicker 
                                            onSelect={handleMediaSelect} 
                                            onClose={() => setIsMediaPickerOpen(false)}
                                            resourceType="image"
                                        />
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Konten Utama</label>
                            <div className="h-64 mb-12">
                                <ReactQuill 
                                    theme="snow" 
                                    value={formData.content} 
                                    onChange={(content) => setFormData(prev => ({...prev, content}))} 
                                    className="h-full"
                                    modules={quillModules}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2 pt-4 border-t">
                            <input 
                                type="checkbox" 
                                id="publish_check"
                                checked={formData.is_published}
                                onChange={(e) => setFormData(prev => ({...prev, is_published: e.target.checked}))}
                                className="w-4 h-4 text-emerald-600 rounded"
                            />
                            <label htmlFor="publish_check" className="text-sm text-gray-700 cursor-pointer">
                                Langsung Publish (tampil di halaman publik)
                            </label>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Batal</Button>
                        <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            Simpan Berita
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default NewsManagement;
